import {
  HeaderBackButton,
  HeaderBackButtonProps,
} from "expo-router/react-navigation";
import { useRouter } from "expo-router";
import { ArrowLeft, ChevronLeft } from "lucide-react-native";
import { useCallback } from "react";
import { Platform } from "react-native";

import { Center } from "@/components/atoms/Center";
import { Icon } from "@/components/atoms/Icon";

import { useTheme } from "@/providers/ThemeProvider";
import { TEST_ID_HEADER_BACK_BUTTON } from "e2e/testId";

/**
 * Container component that renders a themed back button for navigation headers
 *
 * SE-5259: Settings routes (notification-settings, organizations, users,
 * invitations, player-roster, …) are entered on web via the SE-3726
 * `window.history.pushState` + synthetic `popstate` workaround (see
 * `useSettingsActions.webNavigate`). That workaround updates the browser
 * history but never records the transition in React Navigation's internal
 * stack, so from a settings page `router.back()` has no stack entry to pop and
 * silently no-ops — the user is stuck on the page.
 *
 * The handler therefore prefers the navigator's own `router.back()` whenever
 * React Navigation reports it can go back (`canGoBack()`), preserving the
 * existing in-app back behaviour on every other screen. Only when the
 * navigator stack is empty (the desynced settings-page case) AND we are on web
 * with real browser history do we fall back to `window.history.back()`, which
 * honours the pushState entry and leaves the page. Native keeps the original
 * `router.back()` behaviour.
 * @param props - Standard HeaderBackButton props from React Navigation
 */
const StandardHeaderBackButton = (props: HeaderBackButtonProps) => {
  const router = useRouter();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const iconColor = isDark ? "white" : "black";

  const handleBackPress = useCallback(() => {
    if (router.canGoBack()) {
      router.back();
      return;
    }
    if (
      Platform.OS === "web" &&
      typeof window !== "undefined" &&
      window.history.length > 1
    ) {
      window.history.back();
      return;
    }
    router.back();
  }, [router]);

  const renderBackImage = useCallback(
    () => (
      <Center className="size-10 p-1">
        <Icon
          as={Platform.OS === "android" ? ArrowLeft : ChevronLeft}
          size="xl"
          className={`size-full ${isDark ? "text-white" : "text-black"}`}
          color={iconColor}
        />
      </Center>
    ),
    [isDark, iconColor]
  );

  return (
    <HeaderBackButton
      displayMode="minimal"
      {...props}
      onPress={props.onPress || handleBackPress}
      tintColor={iconColor}
      backImage={renderBackImage}
      // SE-6812: a custom `backImage` replaces React Navigation's default
      // accessible name, so this control had none — an unnamed button on every
      // settings route (notification-settings, organizations, users,
      // invitations, player-roster, …). Native e2e resorted to tapping it by
      // screen fraction, which breaks across device sizes and safe areas.
      accessibilityLabel="Go back"
      testID={TEST_ID_HEADER_BACK_BUTTON}
    />
  );
};

export default StandardHeaderBackButton;
